<template>
  <section class="view-stack">
    <article class="dashboard-panel">
      <div class="panel-heading">
        <div>
          <h2>使用记录</h2>
          <p>按时间、分组和 API Key 筛选请求明细，快速查看用量变化。</p>
        </div>
      </div>

      <section class="summary-strip usage-summary-strip">
        <article v-for="item in summaryCards" :key="item.label" class="summary-card">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
        </article>
      </section>

      <div class="usage-filter-bar">
        <div class="usage-quick-range">
          <el-button size="small" :type="quickRange === 'today' ? 'primary' : 'default'" @click="handleQuickRangeClick('today')">今天</el-button>
          <el-button size="small" :type="quickRange === 'week' ? 'primary' : 'default'" @click="handleQuickRangeClick('week')">近 7 天</el-button>
          <el-button size="small" :type="quickRange === 'month' ? 'primary' : 'default'" @click="handleQuickRangeClick('month')">近 30 天</el-button>
          <el-button size="small" @click="handleQuickRangeClick('clear')">清空时间</el-button>
          <el-button class="model-price-button" size="small" icon="el-icon-data-analysis" @click="openModelsDialog">模型与价格</el-button>
        </div>
        <el-date-picker
          v-model="timeRange"
          type="datetimerange"
          unlink-panels
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          value-format="yyyy-MM-dd HH:mm:ss"
          @change="handleTimeRangeChange"
        ></el-date-picker>
        <el-select v-model="filters.balanceId" clearable placeholder="全部分组">
          <el-option
            v-for="item in groupOptions"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          ></el-option>
        </el-select>
        <el-select v-model="filters.apiKey" clearable filterable placeholder="全部 API Key">
          <el-option
            v-for="item in apiKeyOptions"
            :key="item.id"
            :label="item.keyName + ' (' + maskApiKey(item.apiKey) + ')'"
            :value="item.apiKey"
          ></el-option>
        </el-select>
        <div class="usage-filter-actions">
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button @click="resetFilters">重置</el-button>
        </div>
      </div>

      <el-table
        v-loading="loading"
        :data="records"
        class="dashboard-table usage-records-table"
        empty-text="暂无使用记录"
      >
        <el-table-column label="时间" min-width="150">
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.useTime) }}
          </template>
        </el-table-column>
        <el-table-column prop="model" label="模型" min-width="100" show-overflow-tooltip></el-table-column>
        <el-table-column label="耗时" width="130">
          <template slot-scope="scope">
            <div class="usage-timing-cell">
              <span>首字：<strong class="usage-timing-first">{{ formatTimingValue(scope.row.firstSseElapsedMillis) }}</strong></span>
              <span>响应：<strong class="usage-timing-response">{{ formatTimingValue(scope.row.responseCompletedElapsedMillis) }}</strong></span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="API Key" min-width="115" show-overflow-tooltip>
          <template slot-scope="scope">
            <div class="usage-key-cell">
              <strong>{{ scope.row.keyName || '未命名 Key' }}</strong>
              <span>{{ scope.row.apiKeyMasked }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="分组" min-width="360" show-overflow-tooltip>
          <template slot-scope="scope">
            <el-tag :type="scope.row.groupType === 'MONTH_CARD' ? 'warning' : 'success'" size="small">
              {{ getGroupLabel(scope.row) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Token使用" min-width="210">
          <template slot-scope="scope">
            <div class="usage-token-summary-cell">
              <span class="usage-token-summary-cell__input"><em>输入</em>{{ formatNumber(scope.row.billableInputTokens) }}</span>
              <span class="usage-token-summary-cell__write"><em>写缓存</em>{{ formatNumber(scope.row.cacheCreationInputTokens) }}</span>
              <span class="usage-token-summary-cell__read"><em>读缓存</em>{{ formatNumber(scope.row.cachedInputTokens) }}</span>
              <span class="usage-token-summary-cell__output"><em>输出</em>{{ formatNumber(scope.row.outputTokens) }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="消费" width="110">
          <template slot-scope="scope">
            <div class="usage-amount-cell">
              <el-tooltip placement="top" effect="light" popper-class="usage-billing-popper" :open-delay="150">
                <div slot="content" class="usage-billing-tip">{{ getBillingFormula(scope.row) }}</div>
                <strong class="usage-amount-trigger">
                  {{ formatAmount(scope.row.usedAmount) }}
                  <i class="el-icon-info"></i>
                </strong>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="records.length" class="usage-mobile-list">
        <article v-for="record in records" :key="record.id || record.useTime" class="usage-mobile-card">
          <div class="usage-mobile-card__header">
            <strong>{{ formatDateTime(record.useTime) }}</strong>
            <strong class="usage-mobile-card__amount">{{ formatAmount(record.usedAmount) }}</strong>
          </div>
          <div class="usage-mobile-card__model">{{ record.model || '--' }}</div>
          <div class="usage-mobile-card__key">
            <strong>{{ record.keyName || '未命名 Key' }}</strong>
            <span>{{ record.apiKeyMasked }}</span>
          </div>
          <div class="usage-mobile-card__grid">
            <div>
              <span>分组</span>
              <strong>{{ getGroupTypeLabel(record.groupType) }}</strong>
            </div>
            <div>
              <span>首字 / 响应</span>
              <strong>{{ formatTimingValue(record.firstSseElapsedMillis) }} / {{ formatTimingValue(record.responseCompletedElapsedMillis) }}</strong>
            </div>
            <div>
              <span>普通输入</span>
              <strong>{{ formatNumber(record.billableInputTokens) }}</strong>
            </div>
            <div>
              <span>读 / 写缓存</span>
              <strong>{{ formatNumber(record.cachedInputTokens) }} / {{ formatNumber(record.cacheCreationInputTokens) }}</strong>
            </div>
            <div>
              <span>输出</span>
              <strong>{{ formatNumber(record.outputTokens) }}</strong>
            </div>
          </div>
        </article>
      </div>
      <div v-else-if="!loading" class="usage-mobile-empty">暂无使用记录</div>

      <div class="usage-table-meta">
        <span>显示 {{ pageStart }} - {{ pageEnd }} / 共 {{ pagination.total }} 条</span>
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :current-page="pagination.pageNo"
          :page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="pagination.total"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        ></el-pagination>
      </div>
    </article>

    <el-dialog
      title="模型与价格"
      :visible.sync="modelsDialogVisible"
      width="min(96vw, 960px)"
      :close-on-click-modal="false"
    >
      <ModelPriceTable :models="modelPriceModels" :loading="modelPricesLoading" />
    </el-dialog>

  </section>
</template>

<script>
import ModelPriceTable from '../../components/ModelPriceTable.vue';
import { fetchApiKeys, fetchKeyGroupOptions, fetchPublicSupportedModels, fetchSupportedModels } from '../../api/keys';
import { fetchUsageDetails } from '../../api/usage';

function createEmptySummary() {
  return {
    totalRequests: 0,
    successRequests: 0,
    issueRequests: 0,
    inputTokens: 0,
    outputTokens: 0,
    totalTokens: 0,
    cachedInputTokens: 0,
    cacheCreationInputTokens: 0,
    totalAmount: 0,
    successRate: 0
  };
}

export default {
  name: 'UsageView',
  components: { ModelPriceTable },
  data() {
    return {
      loading: false,
      timeRange: [],
      quickRange: 'today',
      filters: {
        balanceId: '',
        apiKey: ''
      },
      groupOptions: [],
      apiKeyOptions: [],
      supportedModels: [],
      modelPriceModels: [],
      modelsDialogVisible: false,
      modelPricesLoading: false,
      records: [],
      summary: createEmptySummary(),
      pagination: {
        pageNo: 1,
        pageSize: 10,
        total: 0
      },
    };
  },
  computed: {
    summaryCards() {
      return [
        {
          label: '请求总数',
          value: this.formatNumber(this.summary.totalRequests)
        },
        {
          label: '总消费',
          value: this.formatAmount(this.summary.totalAmount)
        },
        {
          label: '普通输入',
          value: this.formatCompactNumber(this.summary.inputTokens)
        },
        {
          label: '输出',
          value: this.formatCompactNumber(this.summary.outputTokens)
        },
        {
          label: '读缓存',
          value: this.formatCompactNumber(this.summary.cachedInputTokens)
        },
        {
          label: '写缓存 Token',
          value: this.formatCompactNumber(this.summary.cacheCreationInputTokens)
        },
        {
          label: '总量',
          value: this.formatCompactNumber(this.summary.totalTokens)
        }
      ];
    },
    pageStart() {
      if (!this.pagination.total) {
        return 0;
      }
      return (this.pagination.pageNo - 1) * this.pagination.pageSize + 1;
    },
    pageEnd() {
      if (!this.pagination.total) {
        return 0;
      }
      return Math.min(this.pagination.pageNo * this.pagination.pageSize, this.pagination.total);
    }
  },
  created() {
    this.applyQuickRange('today');
    this.loadGroupOptions();
    this.loadApiKeyOptions();
    this.loadSupportedModels();
    this.loadUsage();
  },
  methods: {
    async loadGroupOptions() {
      try {
        const { data } = await fetchKeyGroupOptions();
        if (data && data.code === 0 && Array.isArray(data.data)) {
          this.groupOptions = data.data;
          return;
        }
        this.groupOptions = [];
      } catch (error) {
        this.groupOptions = [];
      }
    },
    async loadApiKeyOptions() {
      try {
        const { data } = await fetchApiKeys();
        if (data && data.code === 0 && Array.isArray(data.data)) {
          this.apiKeyOptions = data.data;
        }
      } catch (error) {
        this.apiKeyOptions = [];
      }
    },
    async loadSupportedModels() {
      try {
        const { data } = await fetchSupportedModels();
        if (data && data.code === 0 && Array.isArray(data.data)) {
          this.supportedModels = data.data;
          return;
        }
        this.supportedModels = [];
      } catch (error) {
        this.supportedModels = [];
      }
    },
    async openModelsDialog() {
      this.modelsDialogVisible = true;
      if (this.modelPriceModels.length) {
        return;
      }
      this.modelPricesLoading = true;
      try {
        const { data } = await fetchPublicSupportedModels();
        if (!data || data.code !== 0) {
          throw new Error((data && data.message) || 'MODEL_PRICE_LOAD_FAILED');
        }
        this.modelPriceModels = Array.isArray(data.data) ? data.data : [];
      } catch (error) {
        this.$message.error(error.message || '模型价格加载失败，请稍后重试');
      } finally {
        this.modelPricesLoading = false;
      }
    },
    async loadUsage() {
      this.loading = true;
      try {
        const { data } = await fetchUsageDetails(this.buildQueryParams());
        if (!data || data.code !== 0 || !data.data) {
          throw new Error((data && data.message) || 'USAGE_QUERY_FAILED');
        }
        const payload = data.data;
        this.records = Array.isArray(payload.records) ? payload.records : [];
        this.pagination.total = Number(payload.total || 0);
        this.pagination.pageNo = Number(payload.pageNo || this.pagination.pageNo);
        this.pagination.pageSize = Number(payload.pageSize || this.pagination.pageSize);
        this.summary = Object.assign(createEmptySummary(), payload.summary || {});
      } catch (error) {
        this.records = [];
        this.pagination.total = 0;
        this.summary = createEmptySummary();
        this.$message.error(error.message || '使用记录加载失败');
      } finally {
        this.loading = false;
      }
    },
    buildQueryParams() {
      const params = {
        pageNo: this.pagination.pageNo,
        pageSize: this.pagination.pageSize,
        status: 'SUCCESS'
      };
      if (this.timeRange && this.timeRange.length === 2) {
        params.startTime = this.timeRange[0];
        params.endTime = this.timeRange[1];
      }
      if (this.filters.balanceId) {
        params.balanceId = this.filters.balanceId;
      }
      if (this.filters.apiKey) {
        params.apiKey = this.filters.apiKey;
      }
      return params;
    },
    handleSearch() {
      this.pagination.pageNo = 1;
      this.loadUsage();
    },
    resetFilters() {
      this.filters = {
        balanceId: '',
        apiKey: ''
      };
      this.applyQuickRange('today');
      this.pagination.pageNo = 1;
      this.loadUsage();
    },
    handlePageChange(pageNo) {
      this.pagination.pageNo = pageNo;
      this.loadUsage();
    },
    handleSizeChange(pageSize) {
      this.pagination.pageSize = pageSize;
      this.pagination.pageNo = 1;
      this.loadUsage();
    },
    handleTimeRangeChange() {
      this.quickRange = '';
      this.pagination.pageNo = 1;
      this.loadUsage();
    },
    handleQuickRangeClick(type) {
      this.applyQuickRange(type);
      this.pagination.pageNo = 1;
      this.loadUsage();
    },
    applyQuickRange(type) {
      this.quickRange = type;
      if (type === 'clear') {
        this.timeRange = [];
        return;
      }
      const end = new Date();
      const start = new Date(end.getTime());
      if (type === 'today') {
        start.setHours(0, 0, 0, 0);
      } else if (type === 'week') {
        start.setDate(start.getDate() - 6);
        start.setHours(0, 0, 0, 0);
      } else {
        start.setDate(start.getDate() - 29);
        start.setHours(0, 0, 0, 0);
      }
      this.timeRange = [this.formatDateTimeValue(start), this.formatDateTimeValue(end)];
    },
    maskApiKey(apiKey) {
      if (!apiKey) {
        return '';
      }
      if (apiKey.length <= 10) {
        return apiKey;
      }
      return apiKey.slice(0, 6) + '...' + apiKey.slice(-4);
    },
    formatDateTime(value) {
      if (!value) {
        return '--';
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return this.formatDateTimeValue(date);
    },
    formatDateTimeValue(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return year + '-' + month + '-' + day + ' ' + hours + ':' + minutes + ':' + seconds;
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString('zh-CN');
    },
    formatTimingValue(value) {
      if (value === null || value === undefined || value === '') {
        return '--';
      }
      const millis = Number(value);
      return Number.isFinite(millis) ? Math.round(millis / 1000) + '秒' : '--';
    },
    formatCompactNumber(value) {
      const number = Number(value || 0);
      if (number >= 1000000) {
        return (number / 1000000).toFixed(2) + 'M';
      }
      if (number >= 1000) {
        return (number / 1000).toFixed(1) + 'K';
      }
      return String(number);
    },
    findSupportedModel(modelName) {
      const normalized = String(modelName || '').trim().toLowerCase();
      return this.supportedModels
        .filter(item => {
          const model = String(item.model || '').trim().toLowerCase();
          return model && (normalized === model || normalized.indexOf(model + '-') === 0);
        })
        .sort((left, right) => String(right.model || '').length - String(left.model || '').length)[0] || null;
    },
    isLongContextRequest(record) {
      return Number(record.inputTokens || 0) > 272000;
    },
    formatModelPrice(record, field, multiplier) {
      const model = this.findSupportedModel(record.model);
      const value = model && model[field];
      const price = Number(value);
      if (value == null || !Number.isFinite(price)) {
        return '--';
      }
      if (price === 0) {
        return '不支持';
      }
      const factor = Number.isFinite(Number(multiplier)) ? Number(multiplier) : 1;
      return '$' + (price * factor).toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
    },
    getBillingFormula(record) {
      const model = this.findSupportedModel(record.model);
      if (!model) {
        return '单价未加载';
      }
      const cached = Number(record.cachedInputTokens || 0);
      const cacheCreation = Number(record.cacheCreationInputTokens || 0);
      const output = Number(record.outputTokens || 0);
      const longContext = this.isLongContextRequest(record);
      const inputMultiplier = longContext ? 2 : 1;
      const cacheMultiplier = longContext ? 2 : 1;
      const outputMultiplier = longContext ? 1.5 : 1;
      const contextLabel = longContext ? '长上下文（输入超过 272K）：' : '短上下文：';
      return contextLabel + '输入 ' + this.formatNumber(record.billableInputTokens) + '×'
        + this.formatModelPrice(record, 'inputPricePerMillion', inputMultiplier)
        + ' / 1M，读缓存 ' + this.formatNumber(cached) + '×'
        + this.formatModelPrice(record, 'cacheReadPricePerMillion', cacheMultiplier)
        + ' / 1M，写缓存 ' + this.formatNumber(cacheCreation) + '×'
        + this.formatModelPrice(record, 'cacheCreationPricePerMillion')
        + ' / 1M，输出 ' + this.formatNumber(output) + '×'
        + this.formatModelPrice(record, 'outputPricePerMillion', outputMultiplier) + ' / 1M';
    },
    formatAmount(value) {
      return '$' + Number(value || 0).toFixed(4);
    },
    getGroupLabel(record) {
      const matchedOption = this.groupOptions.find(item => String(item.id) === String(record.balanceId));
      if (matchedOption && matchedOption.name) {
        return matchedOption.name;
      }
      if (record.groupType === 'MONTH_CARD') {
        return '日卡分组';
      }
      if (record.groupType === 'BALANCE') {
        return '余额分组';
      }
      return record.groupType || '--';
    },
    getGroupTypeLabel(groupType) {
      if (groupType === 'MONTH_CARD') return '日卡分组';
      if (groupType === 'BALANCE') return '余额分组';
      return groupType || '--';
    }
  }
};
</script>

<style scoped>
.usage-token-summary-cell {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 12px;
  min-width: 0;
}

.usage-token-summary-cell span {
  overflow: hidden;
  color: #5f7480;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.usage-token-summary-cell__input {
  color: #2878c7 !important;
}

.usage-token-summary-cell__write {
  color: #c77b1a !important;
}

.usage-token-summary-cell__read {
  color: #8a5db7 !important;
}

.usage-token-summary-cell__output {
  color: #16836d !important;
}
.usage-token-summary-cell em {
  margin-right: 4px;
  color: inherit;
  opacity: 0.72;
  font-style: normal;
}
.usage-mobile-list,
.usage-mobile-empty {
  display: none;
}

@media (max-width: 640px) {
  .usage-summary-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .usage-summary-strip .summary-card {
    min-height: 96px;
    padding: 14px;
  }

  .usage-quick-range {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    width: 100%;
  }

  .usage-quick-range .el-button {
    width: 100%;
    margin-left: 0;
  }

  .usage-quick-range .model-price-button {
    grid-column: 1 / -1;
  }

  .usage-filter-bar .el-date-editor,
  .usage-filter-bar .el-select {
    width: 100%;
  }

  .usage-filter-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    width: 100%;
  }

  .usage-filter-actions .el-button {
    width: 100%;
    margin-left: 0;
  }

  .usage-records-table {
    display: none;
  }

  .usage-mobile-list {
    display: grid;
    gap: 12px;
  }

  .usage-mobile-card {
    display: grid;
    gap: 10px;
    padding: 14px;
    border: 1px solid #deebe6;
    border-radius: 8px;
    background: #ffffff;
  }

  .usage-mobile-card__header,
  .usage-mobile-card__key {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    min-width: 0;
  }

  .usage-mobile-card__header strong,
  .usage-mobile-card__key strong,
  .usage-mobile-card__key span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .usage-mobile-card__header strong {
    color: #526b78;
    font-size: 12px;
  }

  .usage-mobile-card__amount {
    color: #0f856e !important;
    font-size: 14px !important;
  }

  .usage-mobile-card__model {
    color: #17303b;
    font-size: 15px;
    font-weight: 700;
    overflow-wrap: anywhere;
  }

  .usage-mobile-card__key {
    padding: 9px 10px;
    color: #526b78;
    background: #f7fbfa;
    border: 1px solid #e2eeea;
    border-radius: 6px;
    font-size: 12px;
  }

  .usage-mobile-card__key span {
    color: #0f856e;
    font-family: Consolas, "Courier New", monospace;
  }

  .usage-mobile-card__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    padding-top: 10px;
    border-top: 1px solid #e5efeb;
  }

  .usage-mobile-card__grid div {
    display: grid;
    gap: 4px;
    min-width: 0;
  }

  .usage-mobile-card__grid span {
    color: #718590;
    font-size: 12px;
  }

  .usage-mobile-card__grid strong {
    color: #17303b;
    font-size: 12px;
    overflow-wrap: anywhere;
  }

  .usage-table-meta {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
  }

  .usage-table-meta .el-pagination {
    display: flex;
    width: 100%;
    justify-content: center;
    padding: 0;
    align-self: center;
    white-space: nowrap;
  }

  .usage-table-meta .el-pagination__total,
  .usage-table-meta .el-pagination__sizes {
    display: none;
  }

  .usage-mobile-empty {
    display: block;
    padding: 24px 12px;
    color: #748894;
    text-align: center;
  }
}
</style>
